import { CheckIcon, GhostIcon, SearchIcon } from "lucide-react";
import type { HTMLAttributes } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { cn } from "@/lib/utils";

interface NoRecordsEmptyProps {
  /**
   * The state of the empty view
   * - "no-records": Shows when there are no records at all
   * - "no-filter-results": Shows when no records match current filters
   * - "done": Shows when everything is completed
   */
  state: "no-records" | "no-filter-results" | "done";

  /**
   * Callback when the reset/clear filters button is clicked
   */
  onReset?: () => void;

  /**
   * Callback when the create button is clicked
   */
  onCreate?: () => void;

  /**
   * Custom title for the empty state
   */
  title?: string;

  /**
   * Additional CSS classes
   */
  className?: HTMLAttributes<HTMLDivElement>["className"];
}

export function NoRecordsEmpty({
  state,
  onReset,
  onCreate,
  title,
  className,
}: NoRecordsEmptyProps) {
  const { t } = useTranslation();

  if (state === "no-records") {
    return (
      <div className="flex justify-center mt-10">
        <Empty className={cn("border border-dashed max-w-xl", className)}>
          <EmptyHeader>
            <EmptyMedia variant="icon" className="h-10 w-10">
              <GhostIcon />
            </EmptyMedia>
            <EmptyTitle>{title || t("empty_state.no_records")}</EmptyTitle>
          </EmptyHeader>
          {onCreate && (
            <EmptyContent>
              <Button onClick={onCreate} variant="outline" size="sm">
                {t("empty_state.create_first")}
              </Button>
            </EmptyContent>
          )}
        </Empty>
      </div>
    );
  }

  if (state === "done") {
    return (
      <div className="flex justify-center mt-10">
        <Empty className={cn("border border-dashed max-w-xl", className)}>
          <EmptyHeader>
            <EmptyMedia variant="icon" className="h-10 w-10">
              <CheckIcon className="text-green-500" />
            </EmptyMedia>
            <EmptyTitle>{title || t("empty_state.done")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div className="flex justify-center mt-10">
      <Empty className={cn("border border-dashed max-w-xl", className)}>
        <EmptyHeader>
          <EmptyMedia variant="icon" className="h-10 w-10">
            <SearchIcon />
          </EmptyMedia>
          <EmptyTitle>{title || t("empty_state.no_records_found")}</EmptyTitle>
        </EmptyHeader>
        {onReset && (
          <EmptyContent>
            <Button onClick={onReset} variant="outline" size="sm">
              {t("empty_state.clear_filters")}
            </Button>
          </EmptyContent>
        )}
      </Empty>
    </div>
  );
}
